import React from "react";
import LegalLayout from "../components/LegalLayout";

const NAV = [
  { id: "intro", label: "1. Introduction" },
  { id: "definitions", label: "2. Definitions" },
  { id: "account", label: "3. Account Terms" },
  { id: "pricing", label: "4. Pricing & Payment" },
  { id: "products", label: "5. Product Descriptions" },
  { id: "orders", label: "6. Order Acceptance" },
  { id: "shipping", label: "7. Shipping & Delivery" },
  { id: "returns", label: "8. Returns & Refunds" },
  { id: "prohibited", label: "9. Prohibited Uses" },
  { id: "liability", label: "10. Limitation of Liability" },
  { id: "ip", label: "11. Intellectual Property" },
  { id: "law", label: "12. Governing Law" },
  { id: "support", label: "13. Customer Support" },
  { id: "termination", label: "14. Termination" },
  { id: "general", label: "15. General Provisions" },
  { id: "ack", label: "16. Acknowledgment" },
];

const Terms = () => (
  <LegalLayout
    eyebrow="Gruham · Legal"
    title="Terms of Service"
    lastUpdated="5 June 2026"
    nav={NAV}
  >
    <LegalLayout.Section id="intro" title="1. Introduction & Acceptance of Terms">
      <p>
        Welcome to Gruham ("we," "us," "our," "Company," or "Platform").
        These Terms of Service ("Terms") constitute a legal agreement between you
        ("you," "your," "User" or "Customer") and Gruham By accessing,
        browsing, registering an account, or making purchases on our e-commerce
        platform and website (collectively, the "Platform"), you agree to be
        bound by these Terms.
      </p>
      <p>
        If you do not agree with any part of these Terms, you must immediately
        discontinue use of the Platform. Your continued use of the Platform
        after changes to these Terms constitutes your acceptance of the updated
        Terms.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="definitions" title="2. Definitions">
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Platform:</strong> The Gruham website, mobile application, and all associated services.</li>
        <li><strong>Products:</strong> All goods and items offered for sale on the Platform.</li>
        <li><strong>Order:</strong> A request to purchase Products from the Platform.</li>
        <li><strong>Transaction:</strong> The complete process of ordering, payment, and delivery.</li>
        <li><strong>User:</strong> Any person accessing the Platform, whether or not they have a registered account.</li>
      </ul>
    </LegalLayout.Section>

    <LegalLayout.Section id="account" title="3. Account Terms & Registration">
      <p className="font-semibold">3.1 Account Eligibility</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>You must be at least 18 years of age or have parental/guardian consent.</li>
        <li>You must be a resident of India.</li>
        <li>You must provide accurate, truthful and complete information during registration.</li>
        <li>One person may not maintain multiple accounts without written permission.</li>
      </ul>
      <p className="font-semibold">3.2 Account Creation & Security</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>You are responsible for maintaining the confidentiality of your username and password.</li>
        <li>You agree to immediately notify us of any unauthorized use of your account.</li>
        <li>You are liable for all activities that occur under your account.</li>
        <li>We are not responsible for unauthorized access due to your failure to protect your credentials.</li>
      </ul>
      <p className="font-semibold">3.3 Account Management</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>You must update your profile information to keep it accurate and current.</li>
        <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
        <li>You may delete your account by writing to support@gruham.in.</li>
      </ul>
    </LegalLayout.Section>

    <LegalLayout.Section id="pricing" title="4. Pricing & Payment">
      <p className="font-semibold">4.1 Pricing Information</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>All prices are displayed in Indian Rupees (INR).</li>
        <li>Prices are subject to change without notice; orders will be honoured at the price displayed at the time of purchase.</li>
        <li>All prices include applicable GST, unless otherwise stated.</li>
      </ul>
      <p className="font-semibold">4.2 Payment Methods Accepted</p>
      <p>Currently we accept <strong>Cash on Delivery</strong> only.</p>
      <p className="font-semibold">4.3 Payment Processing & Security</p>
      <p>
        All future digital payments will be processed through secure, PCI-DSS
        compliant payment gateways. We do NOT store complete credit card or
        bank details on our servers.
      </p>
      <p className="font-semibold">4.4 Payment Disputes & Chargebacks</p>
      <p>
        If you dispute a transaction, please contact us at
        support@gruham.in. False chargeback claims may result in account
        suspension and legal action.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="products" title="5. Product Descriptions & Accuracy">
      <p>
        Product descriptions, images, specifications and prices are provided on
        an "as-is" basis. We strive for accuracy but do not guarantee that
        descriptions, images or prices are error-free. Minor variations in
        colour, size or appearance may occur due to photography, lighting and
        monitor settings.
      </p>
      <p>
        Specifications are provided for informational purposes only. Your
        reliance on product descriptions is at your own risk. If a product does
        not meet the advertised description, please notify us within 48 hours
        of delivery.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="orders" title="6. Order Acceptance & Confirmation">
      <p className="font-semibold">6.1 Order Process</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>You select Products and add them to your shopping cart.</li>
        <li>You enter shipping and billing address.</li>
        <li>You select Cash on Delivery as the payment method.</li>
        <li>You receive an order confirmation with order number and details.</li>
      </ul>
      <p className="font-semibold">6.2 Right of Refusal</p>
      <p>
        Placement of an order does NOT automatically constitute acceptance. We
        may reject any order for any reason, including out-of-stock items,
        pricing errors or fraud suspicion. We will notify you of acceptance or
        rejection within 24 hours.
      </p>
      <p className="font-semibold">6.3 Order Cancellation</p>
      <p>
        You may cancel an order within 24 hours of placement by contacting
        support@gruham.in. Orders cannot be cancelled after shipment has
        begun.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="shipping" title="7. Shipping & Delivery">
      <ul className="list-disc pl-5 space-y-1">
        <li>Standard delivery: 5-7 business days.</li>
        <li>Express delivery: 2-3 business days (where available, additional charges apply).</li>
        <li>Delivery timelines are estimates and not guaranteed.</li>
        <li>Orders are not shipped on Sundays and public holidays in India.</li>
        <li>You are responsible for providing accurate shipping addresses.</li>
        <li>Tracking information will be provided via SMS and email once shipment is dispatched.</li>
        <li>Risk of loss/damage passes to you upon delivery to the courier partner.</li>
      </ul>
    </LegalLayout.Section>

    <LegalLayout.Section id="returns" title="8. Returns & Refunds">
      <ul className="list-disc pl-5 space-y-1">
        <li>Returns are accepted within 7 days from the delivery date.</li>
        <li>Products must be unused, undamaged and in original packaging with all tags.</li>
        <li>Refund will be initiated only after we inspect and verify the returned item.</li>
        <li>Inspection typically takes 3-5 business days from receipt.</li>
        <li>Refund crediting typically takes 5-7 business days depending on your bank.</li>
        <li>Return shipping costs are borne by the customer unless the product is defective.</li>
      </ul>
    </LegalLayout.Section>

    <LegalLayout.Section id="prohibited" title="9. Prohibited Uses">
      <p>You agree NOT to use the Platform for any of the following:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Illegal activities, fraud or violation of any applicable laws.</li>
        <li>Harassment, abuse, threats or defamatory content.</li>
        <li>Posting false, misleading or harmful reviews or testimonials.</li>
        <li>Hacking, unauthorized access or attempting to compromise Platform security.</li>
        <li>Uploading viruses, malware or harmful code.</li>
        <li>Scraping, crawling or automated access to the Platform.</li>
        <li>Reselling products purchased as an individual consumer to commercial buyers.</li>
        <li>Impersonating Gruham staff or other users.</li>
        <li>Filing frivolous complaints, chargebacks or legal claims.</li>
      </ul>
    </LegalLayout.Section>

    <LegalLayout.Section id="liability" title="10. Limitation of Liability & Disclaimer">
      <p>
        The Platform and all Products are provided "AS-IS" and "AS-AVAILABLE"
        without any warranties of any kind. Gruham disclaims all
        express, implied, statutory and other warranties, including fitness for
        a particular purpose, merchantability, accuracy, completeness and
        non-infringement.
      </p>
      <p>
        Gruham's maximum liability shall not exceed the total amount
        paid by you in the transaction giving rise to the claim, or ₹5,000
        (whichever is lower).
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="ip" title="11. Intellectual Property Rights">
      <p>
        All content on the Platform, including text, images, videos, logos and
        software, is the property of Gruham or its licensors. You do
        not acquire any ownership rights to any content. Reproducing,
        distributing or using content without permission is prohibited.
      </p>
      <p>
        By submitting reviews, feedback or testimonials, you grant Gruham
        INC. a perpetual, royalty-free license to use such content.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="law" title="12. Governing Law & Jurisdiction">
      <p>
        These Terms of Service shall be governed by and construed in accordance
        with the laws of India. The courts of Rajkot, Gujarat, India shall have
        exclusive jurisdiction over any disputes.
      </p>
      <p>
        Before pursuing legal action, you agree to attempt resolution through
        contact with support@gruham.in. If unresolved, either party may
        approach the Consumer Disputes Redressal Commission or civil court.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="support" title="13. Customer Support & Contact Information">
      <ul className="list-disc pl-5 space-y-1">
        <li>Email: support@gruham.in</li>
        <li>Phone: +91 9664527720</li>
        <li>Operating Hours: Monday to Saturday, 9AM to 7PM</li>
        <li>Registered Address: Copper Build, Police Parade ground road, Nana Mauva, Rajkot, Gujarat, 360005, India</li>
        <li>Response Time: We aim to respond within 24 business hours.</li>
      </ul>
    </LegalLayout.Section>

    <LegalLayout.Section id="termination" title="14. Termination & Suspension of Service">
      <p>
        You may terminate your account at any time by contacting us. We may
        terminate or suspend your account immediately if you violate these
        Terms. Upon termination, your right to access the Platform ceases
        immediately. Your personal data may be retained as required by law.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="general" title="15. General Provisions">
      <p>
        <strong>Entire Agreement:</strong> These Terms of Service, along with
        our Privacy Policy and any other policies posted on the Platform,
        constitute the entire agreement between you and Gruham
      </p>
      <p>
        <strong>Amendments:</strong> We may amend these Terms at any time.
        Material changes will be notified via email or prominent notice on the
        Platform.
      </p>
      <p>
        <strong>Severability:</strong> If any provision of these Terms is found
        to be invalid or unenforceable, the remaining provisions shall continue
        in full effect.
      </p>
      <p>
        <strong>Force Majeure:</strong> Gruham shall not be liable for
        delays or failures resulting from forces beyond our reasonable control.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="ack" title="16. Acknowledgment & Acceptance">
      <p>
        By accessing and using Gruham, you acknowledge that you have
        read, understood and agree to be bound by these Terms of Service. You
        accept full responsibility for your use of the Platform.
      </p>
    </LegalLayout.Section>
  </LegalLayout>
);

export default Terms;
